/**
 * Top-level React error boundary. Catches render-phase exceptions in any
 * descendant tree (routing, screens, modals) and shows a recovery panel so the
 * SPA never goes white-screen. Async errors from network calls are *not*
 * surfaced here — those flow through TanStack Query and per-screen Mantine
 * alerts.
 *
 * The fallback is rendered with PLAIN HTML + inline styles — NOT Mantine
 * components — on purpose: this boundary is mounted ABOVE `<MantineProvider>`
 * (see main.tsx), so any Mantine component here would throw "MantineProvider
 * was not found" while handling the original error, masking it and leaving a
 * white screen (the secondary throw escapes to the outer Sentry boundary's
 * empty fallback). A top-level recovery UI must not depend on app providers —
 * one of them may be exactly what failed. Mantine CSS variables are still safe
 * (they come from the global `styles.css` import, not the React context).
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Forward to Sentry (TASK-013a). When `VITE_SENTRY_DSN` is empty the SDK
    // is initialised with `enabled: false` and this is a no-op. The console
    // line stays for offline debugging — Sentry's own breadcrumb captures
    // the same stack, so the duplicate output is dev-only noise.
    Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
     
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div
          role="alert"
          style={{
            maxWidth: 520,
            margin: '0 auto',
            padding: '48px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            fontFamily:
              'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            color: 'var(--mantine-color-text, #1a1b1e)',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
            Что-то пошло не так
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--mantine-color-dimmed, #868e96)' }}>
            Произошла неожиданная ошибка. Попробуйте перезагрузить страницу.
            Если ошибка повторится — сообщите администратору.
          </p>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontFamily: 'monospace',
              color: 'var(--mantine-color-dimmed, #868e96)',
              wordBreak: 'break-word',
            }}
          >
            {this.state.error.message}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              alignSelf: 'flex-start',
              padding: '8px 16px',
              fontSize: 14,
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              color: '#fff',
              background: 'var(--mantine-color-blue-6, #228be6)',
            }}
          >
            Перезагрузить страницу
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
